import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Play, Lock, Download, Check } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import MediaLightbox from "@/components/shared/MediaLightbox";
import { downloadSingle, downloadZip } from "@/lib/download";
import { useCountdown } from "@/lib/useCountdown";
import { trackEvent } from "@/lib/track";

export default function Album() {
  const { slug } = useParams();
  const [wedding, setWedding] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [error, setError] = useState(false);
  const [notPublished, setNotPublished] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const allSelected = photos?.length > 0 && selected.size === photos.length;
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(photos.map((p) => p.id)));

  // A plain link the server forces to save (built server-side from R2, no
  // fetch()/blob() in the browser) -- same mechanism as the single-file button.
  // With nothing selected, downloads the whole album.
  const downloadChosen = () => {
    const ids = [...selected];
    if (ids.length === 1) {
      downloadSingle(photos.find((p) => p.id === ids[0]));
      trackEvent(slug, "download_single");
    } else {
      downloadZip({ publicSlug: slug, ids: ids.length ? ids : undefined });
      trackEvent(slug, "download_zip", ids.length || photos.length);
    }
  };

  useEffect(() => {
    api.get(`/public/${slug}`)
      .then((r) => {
        setWedding(r.data);
        const theme = r.data.tema_colore || {};
        if (theme.primary) document.documentElement.style.setProperty("--wed-primary", theme.primary);
        if (theme.font) document.documentElement.style.setProperty("--wed-font", `'${theme.font}', serif`);
        document.title = `Album — ${r.data.nome_sposi}`;
        trackEvent(slug, "album_view");
      })
      .catch(() => setError(true));
  }, [slug]);

  const publishCountdown = useCountdown(wedding?.gallery_publish_at && notPublished ? wedding.gallery_publish_at : null);

  const loadPhotos = () => {
    api.get(`/public/${slug}/photos`)
      .then((r) => { setPhotos(r.data); setNotPublished(false); })
      .catch((err) => {
        if (err.response?.status === 403) setNotPublished(true);
        else setError(true);
      });
  };

  useEffect(() => {
    if (!wedding) return;
    loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, wedding]);

  // Once a scheduled publish time hits zero, retry automatically instead of
  // leaving the guest stuck on the locked screen until they refresh by hand.
  useEffect(() => {
    if (publishCountdown?.past) loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishCountdown?.past]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="album-not-found">
        <p className="font-wed text-2xl text-center">Evento non trovato</p>
      </div>
    );
  }

  if (!wedding) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse font-wed text-xl" style={{ color: "var(--wed-primary)" }}>Caricamento…</div>
      </div>
    );
  }

  if (notPublished) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center grain" data-testid="album-not-published">
        <div>
          <Lock size={32} className="mx-auto mb-4" style={{ color: "var(--wed-primary)" }} />
          <p className="font-wed text-2xl mb-2">{wedding.nome_sposi}</p>
          <p className="text-neutral-500 mb-6">L'album non è stato ancora pubblicato. Torna a trovarci più avanti!</p>
          {publishCountdown && !publishCountdown.past && (
            <div className="flex items-center justify-center gap-4 sm:gap-6" data-testid="album-publish-countdown">
              {[[publishCountdown.d, "Giorni"], [publishCountdown.h, "Ore"], [publishCountdown.m, "Minuti"], [publishCountdown.s, "Secondi"]].map(([v, label]) => (
                <div key={label}>
                  <p className="font-wed text-3xl sm:text-4xl" style={{ color: "var(--wed-primary)" }}>{String(v).padStart(2, "0")}</p>
                  <p className="text-xs text-neutral-400 uppercase tracking-wide">{label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen" data-testid="album-page">
      <header className="py-16 px-6 text-center">
        <p className="eyebrow mb-3">Album del matrimonio</p>
        <h1 className="font-wed text-4xl sm:text-5xl">{wedding.nome_sposi}</h1>
        <p className="text-neutral-500 mt-2">{new Date(wedding.data_evento).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</p>
        {photos?.length > 0 && (
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            <button type="button" onClick={toggleSelectAll} className="btn-outline-wed text-sm" data-testid="album-select-all-button">
              {allSelected ? "Deseleziona tutto" : "Seleziona tutto"}
            </button>
            <button
              type="button"
              onClick={downloadChosen}
              className="btn-wed inline-flex items-center gap-2 text-sm"
              data-testid="album-download-button"
            >
              <Download size={15} />
              {selected.size > 0 ? `Scarica selezionate (${selected.size})` : `Scarica tutte (${photos.length})`}
            </button>
          </div>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-12 pb-24">
        {!photos ? (
          <div className="animate-pulse font-wed text-xl py-24 text-center" style={{ color: "var(--wed-primary)" }}>Caricamento…</div>
        ) : photos.length === 0 ? (
          <div className="border border-dashed rounded-3xl py-16 text-center text-neutral-400" style={{ borderColor: "var(--wed-primary)" }} data-testid="album-empty">
            Nessuna foto ancora.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3" data-testid="album-grid">
            {photos.map((p, i) => (
              <div key={p.id} className="relative group">
              <button
                onClick={() => setLightboxIndex(i)}
                className="relative w-full aspect-square overflow-hidden rounded-xl focus:outline-none focus:ring-2 block"
                data-testid={`album-photo-${p.id}`}
              >
                {p.media_type === "video" ? (
                  <>
                    {p.thumbnail_url !== p.url ? (
                      <img src={assetUrl(p.thumbnail_url)} alt={p.caption || "Video"} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    ) : (
                      <video src={assetUrl(p.thumbnail_url)} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" muted preload="metadata" />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <Play size={28} className="text-white drop-shadow" fill="white" />
                    </span>
                  </>
                ) : (
                  <img
                    src={assetUrl(p.thumbnail_url)}
                    alt={p.caption || "Foto matrimonio"}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                )}
                {(p.caption || p.uploaded_by_name) && (
                  <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {p.caption && <p className="text-white text-xs truncate text-left">{p.caption}</p>}
                    {p.uploaded_by_name && <p className="text-white/70 text-[0.65rem] truncate text-left">— {p.uploaded_by_name}</p>}
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleSelect(p.id); }}
                className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors duration-150"
                style={selected.has(p.id) ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)" } : { background: "rgba(255,255,255,0.8)", borderColor: "white" }}
                data-testid={`album-select-${p.id}`}
              >
                {selected.has(p.id) && <Check size={14} className="text-white" />}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); downloadSingle(p, i); trackEvent(slug, "download_single"); }}
                className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity duration-200"
                data-testid={`album-download-${p.id}`}
              >
                <Download size={13} />
              </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {lightboxIndex != null && (
        <MediaLightbox
          items={photos}
          index={lightboxIndex}
          onChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDownload={() => trackEvent(slug, "download_single")}
        />
      )}
    </div>
  );
}
